import React from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ComputerIcon from '@mui/icons-material/Computer';
import LaptopIcon from '@mui/icons-material/Laptop';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TabletIcon from '@mui/icons-material/Tablet';
import StorageIcon from '@mui/icons-material/Storage';
import DnsIcon from '@mui/icons-material/Dns';
import BuildIcon from '@mui/icons-material/Build';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import MemoryIcon from '@mui/icons-material/Memory';

const repairServices = [
  {
    icon: LaptopIcon,
    title: 'Laptop Repair',
    description: 'All PC laptop brands — screen, battery, keyboard, motherboard, liquid damage, and OS issues.',
    path: '/laptop-repair',
  },
  {
    icon: LaptopIcon,
    title: 'Mac Repair',
    description: 'MacBook Air, MacBook Pro, iMac, Mac mini — hardware repair, upgrades, and macOS recovery.',
    path: '/mac-repair',
  },
  {
    icon: ComputerIcon,
    title: 'Desktop Service',
    description: 'PC desktop diagnostics, hardware upgrades, virus removal, OS installation, and custom builds.',
    path: '/desktop-service',
  },
  {
    icon: PhoneIphoneIcon,
    title: 'Smartphone Repair',
    description: 'iPhone, Samsung, Google, and all Android phones — screen, battery, charging port, water damage.',
    path: '/mobile-repair',
  },
  {
    icon: TabletIcon,
    title: 'Tablet Repair',
    description: 'iPad, Samsung Tab, Surface, and Android tablets — screen, battery, software, and hardware repair.',
    path: '/tablet-repair',
  },
  {
    icon: DnsIcon,
    title: 'Server Hardware Repair',
    description: 'Rack and tower servers, NAS, workstations — RAID rebuild, PSU, RAM, storage, and cooling repair.',
    path: '/server-repair',
  },
  {
    icon: StorageIcon,
    title: 'Data Recovery',
    description: 'Professional data recovery from failed hard drives, SSDs, RAID arrays, and flash storage.',
    path: '/data-recovery',
  },
  {
    icon: BuildIcon,
    title: 'Parts Replacement',
    description: 'Genuine replacement parts for laptops, desktops, servers, and mobile devices — all brands.',
    path: '/parts-replacement',
  },
];

const benefits = [
  { icon: BuildIcon,    title: 'All Devices',       description: 'Phones, tablets, laptops, Macs, desktops, and servers' },
  { icon: SpeedIcon,    title: 'Fast Turnaround',   description: 'Most repairs completed within 24–48 hours' },
  { icon: SecurityIcon, title: '90-Day Warranty',   description: 'Warranty on all parts and labor' },
  { icon: MemoryIcon,   title: 'Genuine Parts',     description: 'Quality replacement parts for every brand' },
];

const cardStyle = {
  height: '100%',
  background: 'rgba(255,107,107,0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  border: '1px solid rgba(255,107,107,0.1)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-6px)',
    border: '1px solid rgba(255,107,107,0.28)',
    boxShadow: '0 12px 30px rgba(255,107,107,0.12)',
  },
};

function DeviceRepairPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* Background blobs */}
      <Box sx={{
        position: 'absolute', top: '8%', right: '5%',
        width: 400, height: 400,
        background: 'radial-gradient(circle, rgba(255,107,107,0.1) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '12%', left: '2%',
        width: 280, height: 280,
        background: 'radial-gradient(circle, rgba(255,217,61,0.07) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, py: 10 }}>
        <Container maxWidth="lg">

          {/* Hero */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, fontSize: '0.85rem', display: 'block', mb: 2 }}
            >
              Lynn, MA · Hardware Repair
            </Typography>
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.4rem', md: '3.6rem' }, fontWeight: 800, lineHeight: 1.15, mb: 3, color: '#fff' }}
            >
              Device{' '}
              <Box component="span" className="gradient-text">Repair Services</Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', mb: 5, maxWidth: 580, mx: 'auto', lineHeight: 1.8, fontWeight: 400 }}
            >
              Professional repair for every device — phones, tablets, laptops, Macs, desktops, and servers.
              Backed by 16+ years of enterprise IT experience.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/booking')}
              sx={{
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                color: '#2d3436', fontWeight: 700, px: 5, py: 1.6, fontSize: '1rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, var(--primary) 20%, var(--secondary) 80%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(255,107,107,0.3)',
                },
              }}
            >
              Book a Repair
            </Button>
          </Box>

          {/* Benefits */}
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {benefits.map((b, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{
                  textAlign: 'center', p: 3,
                  background: 'rgba(255,107,107,0.05)',
                  border: '1px solid rgba(255,107,107,0.1)',
                  borderRadius: '14px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '14px',
                    background: 'rgba(255,107,107,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mb: 2, mx: 'auto',
                  }}>
                    {React.createElement(b.icon, { sx: { fontSize: 28, color: 'primary.main' } })}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>{b.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{b.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Service cards */}
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
            All Repair Services
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
            Click any service to see full details and book a repair.
          </Typography>

          <Grid container spacing={3}>
            {repairServices.map((service, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={cardStyle} onClick={() => navigate(service.path)}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: '12px',
                      background: 'rgba(255,107,107,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 2,
                    }}>
                      {React.createElement(service.icon, { sx: { fontSize: 26, color: 'primary.main' } })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, mb: 2 }}>
                      {service.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>Learn More</Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

        </Container>
      </Box>
    </Box>
  );
}

export default DeviceRepairPage;
